import { Component, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';

import { ToastrService } from 'ngx-toastr';
import { ManufacturerService } from '../manufacturers.service';
import {
  ConfigurationService,
  SETTING_KEYS,
} from '../../configuration/configuration.service';

@Component({
  selector: 'add-manufacturers',
  standalone: true,
  imports: [ReactiveFormsModule, RouterModule],
  templateUrl: './add-manufacturers.component.html',
  styleUrls: ['./add-manufacturers.component.scss'],
})
export class AddManufacturersComponent implements OnInit {
  manufacturerForm!: FormGroup;
  isEdit = false;
  manufacturerId: string | null = null;
  /** Preview of the code the server will assign on create (MF series). */
  nextCode: string | null = null;

  // Inject dependencies
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);
  private manufacturerService = inject(ManufacturerService);
  private configService = inject(ConfigurationService);

  /**
   * Fields this client requires besides the name (Configuration > Vendors &
   * Manufacturers > Mandatory manufacturer fields). Empty by default: only
   * the name. The server enforces the same list on save.
   */
  mandatory = new Set<string>();
  /** Set on the first save attempt so the "at least one" messages show. */
  submitted = false;

  ngOnInit(): void {
    this.initializeForm();

    this.configService
      .getValue(SETTING_KEYS.MANUFACTURER_MANDATORY_FIELDS)
      .subscribe((value) => this.applyMandatory(value));

    this.manufacturerId = this.route.snapshot.paramMap.get('id');
    this.isEdit = !!this.manufacturerId;

    if (this.isEdit) {
      this.loadManufacturer();
    } else {
      this.manufacturerService.getNextCode().subscribe({
        next: (res) => (this.nextCode = res?.code || null),
        error: () => (this.nextCode = null),
      });
    }
  }

  private initializeForm(): void {
    this.manufacturerForm = this.fb.group({
      name: ['', Validators.required],
      // Assigned by the server on create; shown in the header, not typed.
      code: [''],
      // Blank brand / contact rows are skipped on save; the client's
      // settings decide whether any are required (applyMandatory).
      brands: this.fb.array([this.fb.control('')]),
      contacts: this.fb.array([this.createContactFormGroup()]),
    });
  }

  private createContactFormGroup(values?: {
    role?: string;
    name?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
  }): FormGroup {
    const group = this.fb.group({
      role: [values?.role || 'SALES_OFFICER', Validators.required],
      name: [values?.name ?? ''],
      email: [values?.email ?? '', [Validators.email]],
      phoneNumber: [values?.phoneNumber ?? ''],
    });
    this.applyContactRequired(group);
    return group;
  }

  /** Adds Validators.required to the contact fields the client made mandatory. */
  private applyContactRequired(group: FormGroup): void {
    const map: Record<string, string> = {
      contactName: 'name',
      contactEmail: 'email',
      contactPhone: 'phoneNumber',
    };
    for (const [setting, control] of Object.entries(map)) {
      if (!this.mandatory.has(setting)) continue;
      const c = group.get(control);
      if (!c || c.hasValidator(Validators.required)) continue;
      c.addValidators(Validators.required);
      c.updateValueAndValidity({ emitEvent: false });
    }
  }

  private applyMandatory(value: unknown): void {
    const fields =
      typeof value === 'string' && value
        ? value.split(',').map((f) => f.trim()).filter(Boolean)
        : [];
    this.mandatory = new Set(fields);
    this.contacts.controls.forEach((g) =>
      this.applyContactRequired(g as FormGroup)
    );
  }

  /** Label suffix: ' *' for a mandatory field. */
  req(field: string): string {
    return this.mandatory.has(field) ? ' *' : '';
  }

  /** Brand names typed so far (blank rows dropped). */
  private filledBrands(): string[] {
    return (this.manufacturerForm.getRawValue().brands as string[])
      .map((b) => (b ?? '').trim())
      .filter(Boolean);
  }

  /** Contact rows with at least one of name / email / phone filled. */
  private filledContacts(): any[] {
    return (this.manufacturerForm.getRawValue().contacts as any[])
      .map((c) => ({
        role: c.role,
        name: (c.name ?? '').trim(),
        email: (c.email ?? '').trim(),
        phoneNumber: (c.phoneNumber ?? '').trim(),
      }))
      .filter((c) => c.name || c.email || c.phoneNumber);
  }

  get brandsMissing(): boolean {
    return this.mandatory.has('brands') && this.filledBrands().length === 0;
  }

  get contactsMissing(): boolean {
    return this.mandatory.has('contacts') && this.filledContacts().length === 0;
  }

  get brands(): FormArray {
    return this.manufacturerForm.get('brands') as FormArray;
  }

  get contacts(): FormArray {
    return this.manufacturerForm.get('contacts') as FormArray;
  }

  addBrand(): void {
    this.brands.push(this.fb.control(''));
  }

  removeBrand(index: number): void {
    if (this.brands.length > 1) this.brands.removeAt(index);
  }

  addContact(): void {
    this.contacts.push(this.createContactFormGroup());
  }

  removeContact(index: number): void {
    if (this.contacts.length > 1) this.contacts.removeAt(index);
  }

  loadManufacturer(): void {
    this.manufacturerService
      .getManufacturerById(this.manufacturerId!)
      .subscribe({
        next: (data: any) => {
          this.manufacturerForm.patchValue({
            name: data.name,
            code: data.code,
          });
          // Identity of an existing manufacturer — read-only, never sent.
          this.manufacturerForm.get('name')?.disable();
          this.manufacturerForm.get('code')?.disable();

          // Set brands. A saved brand keeps its name (products, offers and
          // the POS reference it); only new rows are typed in.
          this.brands.clear();
          data.brands.forEach((brand: any) => {
            this.brands.push(
              this.fb.control({ value: brand.name, disabled: true }, Validators.required),
            );
          });

          // Set contacts
          this.contacts.clear();
          data.contacts.forEach((contact: any) => {
            this.contacts.push(
              this.createContactFormGroup({
                role: contact.role,
                name: contact.contactPerson?.name,
                email: contact.contactPerson?.email,
                phoneNumber: contact.contactPerson?.phoneNumber,
              })
            );
          });
          if (this.brands.length === 0) this.addBrand();
          if (this.contacts.length === 0) this.addContact();
        },
        error: (err) => {
          console.error('Error loading manufacturer:', err);
          this.toastr.error('Failed to load manufacturer data');
        },
      });
  }

  goBack(): void {
    this.router.navigate(['/vendors']);
  }

  submit(): void {
    this.submitted = true;
    if (
      this.manufacturerForm.invalid ||
      this.brandsMissing ||
      this.contactsMissing
    ) {
      this.manufacturerForm.markAllAsTouched();
      this.toastr.error('Please fill in all required fields correctly');
      return;
    }

    // .value drops the disabled controls (on edit: the locked name and code).
    // Brands are read raw — a saved brand's name is locked (disabled) but must
    // stay in the list, or the update would drop that brand.
    const payload = {
      ...this.manufacturerForm.value,
      brands: this.filledBrands(),
      contacts: this.filledContacts(),
    };
    // The server assigns the code on create; it is fixed afterwards.
    delete payload.code;

    if (this.isEdit) {
      this.manufacturerService
        .updateManufacturer(this.manufacturerId!, payload)
        .subscribe({
          next: () => {
            this.toastr.success('Manufacturer updated successfully');
            this.goBack();
          },
          error: (err) => {
            const msg = err?.error?.message;
            this.toastr.error(
              typeof msg === 'string' && msg ? msg : 'Failed to update manufacturer'
            );
          },
        });
    } else {
      this.manufacturerService.addManufacturer(payload).subscribe({
        next: (res: any) => {
          this.toastr.success(
            res?.code
              ? `Manufacturer ${res.code} created`
              : 'Manufacturer created successfully'
          );
          this.goBack();
        },
        error: (err) => {
          const msg = err?.error?.message;
          this.toastr.error(
            typeof msg === 'string' && msg ? msg : 'Failed to create manufacturer'
          );
        },
      });
    }
  }
}
